import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  NodeTypes,
  EdgeTypes,
  ReactFlowProvider,
  OnNodesChange,
  OnEdgesChange,
  OnConnect,
  Connection,
  NodeChange,
  EdgeChange,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Type,
  List,
  ToggleLeft,
  Mail,
  Phone,
  Hash,
  AlignLeft,
  GripVertical,
  ShieldCheck,
  Sparkles,
  SlidersHorizontal,
  Calendar,
  Clock,
  Globe,
  Paperclip,
  Link2,
} from 'lucide-react';
import { FormField, DraggableFieldType } from '../../types/form';
import FormFieldNode from './FormFieldNode';
import AIFieldNode from './AIFieldNode';
import HttpRequestFieldNode from './HttpRequestFieldNode';
import SubmitButtonNode from './SubmitButtonNode';
import ValidateCustomerNode from './ValidateCustomerNode';
import FieldConfigDialog from './FieldConfigDialog';
import AIPromptConfigDialog from './AIPromptConfigDialog';
import HttpRequestConfigDialog from './HttpRequestConfigDialog';
import InsertButtonEdge from './InsertButtonEdge';

// Define available field types for the toolbox
const FIELD_TYPES: DraggableFieldType[] = [
  {
    type: 'text',
    label: 'Text Input',
    icon: 'Type',
    defaultField: {
      fieldType: 'text',
      label: '',
      required: false,
      placeholder: '',
    },
  },
  {
    type: 'textarea',
    label: 'Text Area',
    icon: 'AlignLeft',
    defaultField: {
      fieldType: 'textarea',
      label: '',
      required: false,
      placeholder: '',
    },
  },
  {
    type: 'select',
    label: 'Dropdown',
    icon: 'List',
    defaultField: {
      fieldType: 'select',
      label: '',
      required: false,
      options: [],
    },
  },
  {
    type: 'boolean',
    label: 'Toggle',
    icon: 'ToggleLeft',
    defaultField: {
      fieldType: 'boolean',
      label: '',
      required: false,
    },
  },
  {
    type: 'number',
    label: 'Number',
    icon: 'Hash',
    defaultField: {
      fieldType: 'number',
      label: '',
      required: false,
      placeholder: '0',
    },
  },
  {
    type: 'range',
    label: 'Range',
    icon: 'SlidersHorizontal',
    defaultField: {
      fieldType: 'number',
      label: '',
      required: false,
      rangeType: { min: 0, max: 100 },
    },
  },
  {
    type: 'email',
    label: 'Email',
    icon: 'Mail',
    defaultField: {
      fieldType: 'email',
      label: '',
      required: false,
      placeholder: 'your@email.com',
      validation: {
        type: 'EMAIL',
        errorMessage: 'Please enter a valid email address',
      },
    },
  },
  {
    type: 'phone_number',
    label: 'Phone',
    icon: 'Phone',
    defaultField: {
      fieldType: 'phone_number',
      label: '',
      required: false,
      placeholder: '+1 (555) 000-0000',
      validation: {
        type: 'PHONE',
        errorMessage: 'Please enter a valid phone number',
      },
    },
  },
  {
    type: 'url',
    label: 'URL',
    icon: 'Link2',
    defaultField: {
      fieldType: 'url',
      label: '',
      required: false,
      placeholder: 'https://example.com',
      validation: {
        type: 'URL',
        errorMessage: 'Please enter a valid URL',
      },
    },
  },
  {
    type: 'date',
    label: 'Date Picker',
    icon: 'Calendar',
    defaultField: {
      fieldType: 'date',
      label: '',
      required: false,
    },
  },
  {
    type: 'time',
    label: 'Time Picker',
    icon: 'Clock',
    defaultField: {
      fieldType: 'time',
      label: '',
      required: false,
    },
  },
  {
    type: 'file',
    label: 'File Upload',
    icon: 'Paperclip',
    defaultField: {
      fieldType: 'file',
      label: '',
      required: false,
      fileConfig: {
        acceptedTypes: ['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'jpeg'],
        maxFiles: 1,
        maxSizeMb: 10,
      },
    },
  },
];

// Strip conditionals that no longer make sense after a structural change:
// the source field was deleted, or a reorder moved it to (or past) the
// dependent field. The agent backend also drops invalid rules at ingest.
const sanitizeConditionals = (fields: FormField[]): FormField[] =>
  fields.map(f => {
    if (!f.conditional) return f;
    const source = fields.find(s => s.fieldId === f.conditional!.dependsOn);
    if (!source || source.order >= f.order) {
      const { conditional: _conditional, ...rest } = f;
      return rest as FormField;
    }
    return f;
  });

const getIconComponent = (iconName: string) => {
  const icons: Record<string, React.ReactNode> = {
    Type: <Type size={16} />,
    AlignLeft: <AlignLeft size={16} />,
    List: <List size={16} />,
    ToggleLeft: <ToggleLeft size={16} />,
    Mail: <Mail size={16} />,
    Phone: <Phone size={16} />,
    Hash: <Hash size={16} />,
    ShieldCheck: <ShieldCheck size={16} />,
    Sparkles: <Sparkles size={16} />,
    SlidersHorizontal: <SlidersHorizontal size={16} />,
    Calendar: <Calendar size={16} />,
    Clock: <Clock size={16} />,
    Globe: <Globe size={16} />,
    Paperclip: <Paperclip size={16} />,
    Link2: <Link2 size={16} />,
  };
  return icons[iconName] || <Type size={16} />;
};

interface FormBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
  onFlowError?: (error: string | null) => void;
}

const NODE_HEIGHT = 220; // Node height (~120px) + gap (100px)
const NODE_X_POSITION = 100;
const SUBMIT_NODE_ID = 'submit-button';

const FormBuilderInner: React.FC<FormBuilderProps> = ({ fields, onChange, onFlowError }) => {
  const theme = useTheme();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [httpDialogOpen, setHttpDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [pendingFieldType, setPendingFieldType] = useState<DraggableFieldType | null>(null);
  const [insertMenuAnchor, setInsertMenuAnchor] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [insertContext, setInsertContext] = useState<{ sourceId: string; targetId: string } | null>(
    null
  );
  const [insertPosition, setInsertPosition] = useState<{
    sourceId: string;
    targetId: string;
  } | null>(null);

  // Track if we're currently dragging to prevent position resets
  const isDraggingRef = useRef(false);
  // Store node positions separately
  const nodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  // Store onFlowError callback in ref to avoid stale closures
  const onFlowErrorRef = useRef(onFlowError);
  onFlowErrorRef.current = onFlowError;
  // Ref for setEdges to avoid initialization order issues
  const setEdgesRef = useRef<React.Dispatch<React.SetStateAction<Edge[]>>>(() => {});
  // Ref for handleInsertClick to avoid circular dependency with createEdges
  const handleInsertClickRef = useRef<
    (sourceId: string, targetId: string, mouseX: number, mouseY: number) => void
  >(() => {});
  // Flag to force position recalculation (e.g. after insertion)
  const forceRepositionRef = useRef(false);

  // Check if a customer_verify field already exists
  const hasVerifyField = fields.some(f => f.fieldType === 'customer_verify');

  // Stable onInsert callback that delegates to the ref
  const onInsertStable = useCallback(
    (sourceId: string, targetId: string, mouseX: number, mouseY: number) => {
      handleInsertClickRef.current(sourceId, targetId, mouseX, mouseY);
    },
    []
  );

  // Create node data handlers
  const createNodeHandlers = useCallback(
    (fieldId: string) => ({
      onEdit: () => {
        const f = fields.find(fi => fi.fieldId === fieldId);
        if (f) {
          // customer_verify fields don't have a config dialog
          if (f.fieldType === 'customer_verify') return;
          setEditingField(f);
          if (f.fieldType === 'ai') {
            setAiDialogOpen(true);
          } else if (f.fieldType === 'http_request') {
            setHttpDialogOpen(true);
          } else {
            setConfigDialogOpen(true);
          }
        }
      },
      onDelete: () => {
        const newFields = fields.filter(f => f.fieldId !== fieldId);
        const reorderedFields = newFields.map((f, idx) => ({ ...f, order: idx + 1 }));
        // Clean up position ref and recalculate positions for remaining fields
        nodePositionsRef.current.delete(fieldId);
        reorderedFields.forEach((f, idx) => {
          nodePositionsRef.current.set(f.fieldId, {
            x: NODE_X_POSITION,
            y: idx * NODE_HEIGHT + 20,
          });
        });
        forceRepositionRef.current = true;

        // Bridge edges: connect the incoming source to the outgoing target
        setEdgesRef.current(currentEdges => {
          const incomingEdge = currentEdges.find(e => e.target === fieldId);
          const outgoingEdge = currentEdges.find(e => e.source === fieldId);
          let newEdges = currentEdges.filter(e => e.source !== fieldId && e.target !== fieldId);

          if (incomingEdge && outgoingEdge) {
            newEdges.push({
              id: `edge-${incomingEdge.source}-${outgoingEdge.target}`,
              source: incomingEdge.source,
              target: outgoingEdge.target,
              type: 'insertButtonEdge',
              animated: false,
              style: { stroke: theme.palette.primary.main, strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: theme.palette.primary.main,
              },
              data: { onInsert: onInsertStable },
            });
          }

          return newEdges;
        });

        onChange(sanitizeConditionals(reorderedFields));
      },
    }),
    [fields, onChange, theme.palette.primary.main, onInsertStable]
  );

  // Validate edge flow: traverse edges from start to Submit, return ordered field IDs or error
  const validateEdgeFlow = useCallback(
    (
      currentEdges: Edge[],
      fieldIds: string[]
    ): { valid: boolean; error?: string; orderedFieldIds?: string[] } => {
      if (fieldIds.length === 0) return { valid: true, orderedFieldIds: [] };

      // Build adjacency maps
      const outgoing = new Map<string, string>();
      const incoming = new Map<string, string>();
      for (const edge of currentEdges) {
        outgoing.set(edge.source, edge.target);
        incoming.set(edge.target, edge.source);
      }

      // Find start field: the field that has no incoming edge from another field
      let startFieldId: string | undefined;
      // Find field with no incoming edge from another field
      const fieldsWithIncoming = new Set<string>();
      for (const edge of currentEdges) {
        if (fieldIds.includes(edge.target)) {
          fieldsWithIncoming.add(edge.target);
        }
      }
      // Fields without incoming from other fields
      const candidates = fieldIds.filter(id => !fieldsWithIncoming.has(id));
      if (candidates.length === 0) {
        return {
          valid: false,
          error: 'All fields must be connected in a single chain leading to Submit Form',
        };
      }
      startFieldId = candidates[0];

      // Traverse from start following outgoing edges
      const orderedFieldIds: string[] = [];
      const visited = new Set<string>();
      let current: string | undefined = startFieldId;

      while (current && fieldIds.includes(current)) {
        if (visited.has(current)) {
          return { valid: false, error: 'Cycle detected in field connections' };
        }
        visited.add(current);
        orderedFieldIds.push(current);
        current = outgoing.get(current);
      }

      // Check that the chain ends at Submit
      const lastField = orderedFieldIds[orderedFieldIds.length - 1];
      if (outgoing.get(lastField) !== SUBMIT_NODE_ID) {
        return {
          valid: false,
          error:
            'All fields must be connected to Submit Form. Reconnect the edges to form a complete chain.',
        };
      }

      // Check all fields were visited
      if (orderedFieldIds.length !== fieldIds.length) {
        const disconnected = fieldIds.filter(id => !visited.has(id));
        return {
          valid: false,
          error: `Disconnected field(s): ${disconnected.join(', ')}. All fields must be connected in a chain leading to Submit Form.`,
        };
      }

      return { valid: true, orderedFieldIds };
    },
    []
  );

  // Convert fields to nodes (including submit button)
  const createNodes = useCallback((): Node[] => {
    const allNodes: Node[] = [];

    // Add field nodes (including customer_verify)
    const fieldNodes: Node[] = fields.map((field, index) => {
      // Use stored position if available, otherwise calculate default
      const storedPosition = nodePositionsRef.current.get(field.fieldId);
      const defaultY = index * NODE_HEIGHT + 20;
      const position = storedPosition || { x: NODE_X_POSITION, y: defaultY };

      // Store the position if it's new
      if (!storedPosition) {
        nodePositionsRef.current.set(field.fieldId, position);
      }

      // Determine node type
      let nodeType = 'formField';
      if (field.fieldType === 'ai') nodeType = 'aiField';
      else if (field.fieldType === 'http_request') nodeType = 'httpRequestField';
      else if (field.fieldType === 'customer_verify') nodeType = 'validateCustomer';

      return {
        id: field.fieldId,
        type: nodeType,
        position,
        draggable: true,
        data:
          field.fieldType === 'customer_verify'
            ? {
                verificationMethod: field.verificationMethod || 'email',
                onDelete: createNodeHandlers(field.fieldId).onDelete,
              }
            : {
                field,
                ...createNodeHandlers(field.fieldId),
                ...(field.fieldType === 'ai' || field.fieldType === 'http_request'
                  ? { allFields: fields }
                  : {}),
              },
      };
    });

    allNodes.push(...fieldNodes);

    // Add submit button node at the end
    const submitPosition = {
      x: NODE_X_POSITION + 50,
      y: fields.length * NODE_HEIGHT + 20,
    };

    const submitNode: Node = {
      id: SUBMIT_NODE_ID,
      type: 'submitButton',
      position: submitPosition,
      draggable: false,
      selectable: false,
      data: { label: 'Submit Form' },
    };

    allNodes.push(submitNode);

    return allNodes;
  }, [fields, createNodeHandlers]);

  // Create edges to connect nodes in sequence
  const createEdges = useCallback((): Edge[] => {
    const edges: Edge[] = [];
    const sortedFields = [...fields].sort((a, b) => a.order - b.order);
    const insertEdgeData = { onInsert: onInsertStable };

    // Connect field nodes in sequence
    for (let i = 0; i < sortedFields.length; i++) {
      const sourceId = sortedFields[i].fieldId;
      const targetId = i < sortedFields.length - 1 ? sortedFields[i + 1].fieldId : SUBMIT_NODE_ID;

      // Use warning color for edges from customer_verify
      const isFromVerify = sortedFields[i].fieldType === 'customer_verify';
      const edgeColor = isFromVerify ? theme.palette.warning.main : theme.palette.primary.main;

      edges.push({
        id: `edge-${sourceId}-${targetId}`,
        source: sourceId,
        target: targetId,
        type: 'insertButtonEdge',
        animated: false,
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: edgeColor,
        },
        data: insertEdgeData,
      });
    }

    return edges;
  }, [fields, theme.palette.primary.main, theme.palette.warning.main, onInsertStable]);

  const [nodes, setNodes, onNodesChange] = useNodesState(createNodes());
  const [edges, setEdges, onEdgesChangeBase] = useEdgesState(createEdges());
  setEdgesRef.current = setEdges;

  // Helper to reorder fields based on edge traversal
  const reorderFieldsByEdges = useCallback(
    (currentEdges: Edge[]) => {
      const fieldIds = fields.map(f => f.fieldId);
      const result = validateEdgeFlow(currentEdges, fieldIds);
      onFlowErrorRef.current?.(result.valid ? null : result.error || null);
      if (result.valid && result.orderedFieldIds) {
        const newFields = result.orderedFieldIds
          .map((id, idx) => {
            const field = fields.find(f => f.fieldId === id);
            return field ? { ...field, order: idx + 1 } : null;
          })
          .filter((f): f is FormField => f !== null);
        if (newFields.length === fields.length) {
          const orderChanged = newFields.some(
            (f, idx) => fields[idx]?.fieldId !== f.fieldId || fields[idx]?.order !== f.order
          );
          if (orderChanged) {
            onChange(sanitizeConditionals(newFields));
          }
        }
      }
    },
    [fields, validateEdgeFlow, onChange]
  );

  // Handle edge changes (deletion via Backspace or selection)
  const handleEdgesChange: OnEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChangeBase(changes);
      // After changes applied, validate on next tick
      setTimeout(() => {
        setEdges(currentEdges => {
          reorderFieldsByEdges(currentEdges);
          return currentEdges;
        });
      }, 0);
    },
    [onEdgesChangeBase, setEdges, reorderFieldsByEdges]
  );

  // Handle new connection (user drags from source handle to target handle)
  const handleConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      // Reject self-loops
      if (connection.source === connection.target) return;
      // Reject connections FROM Submit node
      if (connection.source === SUBMIT_NODE_ID) return;

      setEdges(currentEdges => {
        // Enforce linear flow: source can only have 1 outgoing edge
        let newEdges = currentEdges.filter(e => e.source !== connection.source);
        // Enforce linear flow: target can only have 1 incoming edge
        newEdges = newEdges.filter(e => e.target !== connection.target);

        // Add new edge
        const newEdge: Edge = {
          id: `edge-${connection.source}-${connection.target}`,
          source: connection.source!,
          target: connection.target!,
          type: 'insertButtonEdge',
          animated: false,
          style: { stroke: theme.palette.primary.main, strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: theme.palette.primary.main,
          },
          data: { onInsert: onInsertStable },
        };
        newEdges = [...newEdges, newEdge];

        // Validate and reorder
        setTimeout(() => reorderFieldsByEdges(newEdges), 0);

        return newEdges;
      });
    },
    [setEdges, theme.palette.primary.main, reorderFieldsByEdges, onInsertStable]
  );

  // Update nodes when fields change (but only if not dragging)
  useEffect(() => {
    if (!isDraggingRef.current) {
      // Check if fields actually changed (new field added or field removed)
      const currentFieldIds = new Set(fields.map(f => f.fieldId));
      const specialNodeIds = new Set([SUBMIT_NODE_ID]);
      const nodeFieldIds = new Set(nodes.filter(n => !specialNodeIds.has(n.id)).map(n => n.id));

      const fieldsChanged =
        currentFieldIds.size !== nodeFieldIds.size ||
        fields.some(f => !nodeFieldIds.has(f.fieldId)) ||
        nodes.filter(n => !specialNodeIds.has(n.id)).some(n => !currentFieldIds.has(n.id));

      if (fieldsChanged) {
        const allNodes: Node[] = [];
        const forceReposition = forceRepositionRef.current;
        forceRepositionRef.current = false;

        // Reset positions for new fields only
        const fieldNodes: Node[] = fields.map((field, index) => {
          const existingNode = nodes.find(n => n.id === field.fieldId);
          const storedPosition = nodePositionsRef.current.get(field.fieldId);

          let position;
          if (forceReposition && storedPosition) {
            position = storedPosition;
          } else if (existingNode) {
            position = existingNode.position;
          } else if (storedPosition) {
            position = storedPosition;
          } else {
            position = { x: NODE_X_POSITION, y: index * NODE_HEIGHT + 20 };
            nodePositionsRef.current.set(field.fieldId, position);
          }

          let nodeType = 'formField';
          if (field.fieldType === 'ai') nodeType = 'aiField';
          else if (field.fieldType === 'http_request') nodeType = 'httpRequestField';
          else if (field.fieldType === 'customer_verify') nodeType = 'validateCustomer';

          return {
            id: field.fieldId,
            type: nodeType,
            position,
            draggable: true,
            data:
              field.fieldType === 'customer_verify'
                ? {
                    verificationMethod: field.verificationMethod || 'email',
                    onDelete: createNodeHandlers(field.fieldId).onDelete,
                  }
                : {
                    field,
                    ...createNodeHandlers(field.fieldId),
                    ...(field.fieldType === 'ai' || field.fieldType === 'http_request'
                      ? { allFields: fields }
                      : {}),
                  },
          };
        });

        allNodes.push(...fieldNodes);

        // Add submit button at the end
        allNodes.push({
          id: SUBMIT_NODE_ID,
          type: 'submitButton',
          position: { x: NODE_X_POSITION + 50, y: fields.length * NODE_HEIGHT + 20 },
          draggable: false,
          selectable: false,
          data: { label: 'Submit Form' },
        });

        setNodes(allNodes);
        setEdges(createEdges());
      } else {
        // Just update node data without changing positions
        setNodes(currentNodes =>
          currentNodes.map(node => {
            if (node.id === SUBMIT_NODE_ID) {
              return {
                ...node,
                position: {
                  x: NODE_X_POSITION + 50,
                  y: fields.length * NODE_HEIGHT + 20,
                },
              };
            }
            const field = fields.find(f => f.fieldId === node.id);
            if (field) {
              let nodeType = 'formField';
              if (field.fieldType === 'ai') nodeType = 'aiField';
              else if (field.fieldType === 'http_request') nodeType = 'httpRequestField';
              else if (field.fieldType === 'customer_verify') nodeType = 'validateCustomer';

              return {
                ...node,
                type: nodeType,
                data:
                  field.fieldType === 'customer_verify'
                    ? {
                        verificationMethod: field.verificationMethod || 'email',
                        onDelete: createNodeHandlers(field.fieldId).onDelete,
                      }
                    : {
                        field,
                        ...createNodeHandlers(field.fieldId),
                        ...(field.fieldType === 'ai' || field.fieldType === 'http_request'
                          ? { allFields: fields }
                          : {}),
                      },
              };
            }
            return node;
          })
        );
      }
    }
  }, [fields, createNodeHandlers, setNodes, setEdges, createEdges]);

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      formField: FormFieldNode,
      aiField: AIFieldNode,
      httpRequestField: HttpRequestFieldNode,
      submitButton: SubmitButtonNode,
      validateCustomer: ValidateCustomerNode,
    }),
    []
  );

  const edgeTypes: EdgeTypes = useMemo(() => ({ insertButtonEdge: InsertButtonEdge }), []);

  const handleInsertClick = useCallback(
    (sourceId: string, targetId: string, mouseX: number, mouseY: number) => {
      setInsertMenuAnchor({ mouseX, mouseY });
      setInsertContext({ sourceId, targetId });
    },
    []
  );
  handleInsertClickRef.current = handleInsertClick;

  const handleDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    fieldType: DraggableFieldType
  ) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(fieldType));
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      const fieldType: DraggableFieldType = JSON.parse(data);

      // Single-instance guard for customer_verify
      if (fieldType.type === 'customer_verify' && hasVerifyField) return;

      // customer_verify: add directly without config dialog
      if (fieldType.type === 'customer_verify') {
        const newField: FormField = {
          fieldId: `verify_customer_${Date.now()}`,
          label: 'Verify Customer',
          fieldType: 'customer_verify',
          required: false,
          verificationMethod: 'email',
          order: fields.length + 1,
        };

        // Position based on current field count
        const newPosition = { x: NODE_X_POSITION, y: fields.length * NODE_HEIGHT + 20 };
        nodePositionsRef.current.set(newField.fieldId, newPosition);

        // Insert edges
        setEdges(currentEdges => {
          const edgeStyle = { stroke: theme.palette.primary.main, strokeWidth: 2 };
          const marker = {
            type: MarkerType.ArrowClosed as const,
            color: theme.palette.primary.main,
          };
          const insertEdgeData = { onInsert: onInsertStable };
          const edgeDefaults = {
            type: 'insertButtonEdge' as const,
            animated: false,
            data: insertEdgeData,
          };

          if (fields.length === 0) {
            return [
              {
                ...edgeDefaults,
                id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
                source: newField.fieldId,
                target: SUBMIT_NODE_ID,
                style: edgeStyle,
                markerEnd: marker,
              },
            ];
          }

          const edgeToSubmit = currentEdges.find(e => e.target === SUBMIT_NODE_ID);
          if (edgeToSubmit) {
            const newEdges = currentEdges.filter(e => e.id !== edgeToSubmit.id);
            newEdges.push({
              ...edgeDefaults,
              id: `edge-${edgeToSubmit.source}-${newField.fieldId}`,
              source: edgeToSubmit.source,
              target: newField.fieldId,
              style: edgeStyle,
              markerEnd: marker,
            });
            newEdges.push({
              ...edgeDefaults,
              id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
              source: newField.fieldId,
              target: SUBMIT_NODE_ID,
              style: edgeStyle,
              markerEnd: marker,
            });
            return newEdges;
          }

          return [
            ...currentEdges,
            {
              ...edgeDefaults,
              id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
              source: newField.fieldId,
              target: SUBMIT_NODE_ID,
              style: edgeStyle,
              markerEnd: marker,
            },
          ];
        });

        onChange([...fields, newField]);
        return;
      }

      setPendingFieldType(fieldType);
      setEditingField(null);
      if (fieldType.type === 'ai') {
        setAiDialogOpen(true);
      } else if (fieldType.type === 'http_request') {
        setHttpDialogOpen(true);
      } else {
        setConfigDialogOpen(true);
      }
    },
    [fields, hasVerifyField, onChange, setEdges, theme.palette.primary.main, onInsertStable]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleInsertField = useCallback(
    (newField: FormField, sourceId: string, targetId: string) => {
      const sortedFields = [...fields].sort((a, b) => a.order - b.order);
      // Determine insert index
      const srcIdx = sortedFields.findIndex(f => f.fieldId === sourceId);
      const insertIndex = srcIdx >= 0 ? srcIdx + 1 : sortedFields.length;

      // Splice new field into sorted array
      const updatedFields = [...sortedFields];
      updatedFields.splice(insertIndex, 0, newField);

      // Recalculate order and positions
      const reorderedFields = updatedFields.map((f, idx) => {
        const order = idx + 1;
        const position = { x: NODE_X_POSITION, y: idx * NODE_HEIGHT + 20 };
        nodePositionsRef.current.set(f.fieldId, position);
        return { ...f, order };
      });

      // Update edges: remove source→target, add source→new and new→target
      setEdges(currentEdges => {
        const edgeStyle = { stroke: theme.palette.primary.main, strokeWidth: 2 };
        const marker = { type: MarkerType.ArrowClosed as const, color: theme.palette.primary.main };
        const insertEdgeData = { onInsert: onInsertStable };
        const edgeDefaults = {
          type: 'insertButtonEdge' as const,
          animated: false,
          data: insertEdgeData,
        };

        // Remove the old edge between source and target
        const newEdges = currentEdges.filter(
          e => !(e.source === sourceId && e.target === targetId)
        );

        newEdges.push({
          ...edgeDefaults,
          id: `edge-${sourceId}-${newField.fieldId}`,
          source: sourceId,
          target: newField.fieldId,
          style: edgeStyle,
          markerEnd: marker,
        });

        newEdges.push({
          ...edgeDefaults,
          id: `edge-${newField.fieldId}-${targetId}`,
          source: newField.fieldId,
          target: targetId,
          style: edgeStyle,
          markerEnd: marker,
        });

        return newEdges;
      });

      // Signal that all node positions should be recalculated from nodePositionsRef
      forceRepositionRef.current = true;
      onChange(sanitizeConditionals(reorderedFields));
    },
    [fields, setEdges, theme.palette.primary.main, onInsertStable, onChange]
  );

  const handleInsertFieldType = useCallback(
    (fieldType: DraggableFieldType) => {
      setInsertMenuAnchor(null);

      // Single-instance guard for customer_verify
      if (fieldType.type === 'customer_verify' && hasVerifyField) return;

      // customer_verify: insert directly without config dialog
      if (fieldType.type === 'customer_verify' && insertContext) {
        const newField: FormField = {
          fieldId: `verify_customer_${Date.now()}`,
          label: 'Verify Customer',
          fieldType: 'customer_verify',
          required: false,
          verificationMethod: 'email',
          order: fields.length + 1,
        };
        handleInsertField(newField, insertContext.sourceId, insertContext.targetId);
        setInsertContext(null);
        return;
      }

      if (insertContext) {
        setInsertPosition(insertContext);
      }
      setPendingFieldType(fieldType);
      setEditingField(null);
      if (fieldType.type === 'ai') {
        setAiDialogOpen(true);
      } else if (fieldType.type === 'http_request') {
        setHttpDialogOpen(true);
      } else {
        setConfigDialogOpen(true);
      }
    },
    [insertContext, hasVerifyField, fields.length, handleInsertField]
  );

  const handleFieldSave = (field: FormField) => {
    if (editingField) {
      // Editing existing field
      const newFields = fields.map(f => (f.fieldId === field.fieldId ? field : f));
      onChange(sanitizeConditionals(newFields));
    } else if (insertPosition) {
      // Inserting field between two nodes
      const newField: FormField = {
        ...field,
        order: fields.length + 1, // Will be recalculated in handleInsertField
      };
      handleInsertField(newField, insertPosition.sourceId, insertPosition.targetId);
    } else {
      // Adding new field at the end
      const newPosition = { x: NODE_X_POSITION, y: fields.length * NODE_HEIGHT + 20 };
      nodePositionsRef.current.set(field.fieldId, newPosition);

      const newField: FormField = {
        ...field,
        order: fields.length + 1,
      };

      // Insert edges: splice new field into the chain before Submit
      setEdges(currentEdges => {
        const edgeStyle = { stroke: theme.palette.primary.main, strokeWidth: 2 };
        const marker = { type: MarkerType.ArrowClosed as const, color: theme.palette.primary.main };
        const insertEdgeData = { onInsert: onInsertStable };
        const edgeDefaults = {
          type: 'insertButtonEdge' as const,
          animated: false,
          data: insertEdgeData,
        };

        if (fields.length === 0) {
          // First field: connect directly to Submit
          return [
            {
              ...edgeDefaults,
              id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
              source: newField.fieldId,
              target: SUBMIT_NODE_ID,
              style: edgeStyle,
              markerEnd: marker,
            },
          ];
        }

        // Find the edge targeting Submit
        const edgeToSubmit = currentEdges.find(e => e.target === SUBMIT_NODE_ID);
        if (edgeToSubmit) {
          const newEdges = currentEdges.filter(e => e.id !== edgeToSubmit.id);
          newEdges.push({
            ...edgeDefaults,
            id: `edge-${edgeToSubmit.source}-${newField.fieldId}`,
            source: edgeToSubmit.source,
            target: newField.fieldId,
            style: edgeStyle,
            markerEnd: marker,
          });
          newEdges.push({
            ...edgeDefaults,
            id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
            source: newField.fieldId,
            target: SUBMIT_NODE_ID,
            style: edgeStyle,
            markerEnd: marker,
          });
          return newEdges;
        }

        // Fallback: just add edge from new field to Submit
        return [
          ...currentEdges,
          {
            ...edgeDefaults,
            id: `edge-${newField.fieldId}-${SUBMIT_NODE_ID}`,
            source: newField.fieldId,
            target: SUBMIT_NODE_ID,
            style: edgeStyle,
            markerEnd: marker,
          },
        ];
      });

      onChange([...fields, newField]);
    }
    setConfigDialogOpen(false);
    setHttpDialogOpen(false);
    setEditingField(null);
    setPendingFieldType(null);
    setInsertPosition(null);
    setInsertContext(null);
  };

  const handleNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Filter out position changes for non-draggable nodes (submit button)
      const nonDraggableNodes = new Set([SUBMIT_NODE_ID]);
      const filteredChanges = changes.filter(change => {
        if ('id' in change && nonDraggableNodes.has(change.id)) {
          return change.type !== 'position';
        }
        return true;
      });

      // Track dragging state
      const dragStartChange = filteredChanges.find(
        (c): c is NodeChange & { dragging: boolean } =>
          c.type === 'position' && 'dragging' in c && c.dragging === true
      );
      const dragEndChange = filteredChanges.find(
        (c): c is NodeChange & { dragging: boolean } =>
          c.type === 'position' && 'dragging' in c && c.dragging === false
      );

      if (dragStartChange) {
        isDraggingRef.current = true;
      }

      // Apply the changes
      onNodesChange(filteredChanges);

      // Update stored positions for position changes (exclude submit button)
      filteredChanges.forEach(change => {
        if (
          change.type === 'position' &&
          'position' in change &&
          change.position &&
          'id' in change &&
          change.id !== SUBMIT_NODE_ID
        ) {
          nodePositionsRef.current.set(change.id, change.position);
        }
      });

      // Nodes can be dragged freely for visual layout; order comes from edges only
      if (dragEndChange) {
        isDraggingRef.current = false;
      }
    },
    [onNodesChange]
  );

  // Compute hasVerificationBefore for a given field order
  const getHasVerificationBefore = useCallback(
    (fieldOrder: number) => {
      return fields.some(f => f.fieldType === 'customer_verify' && f.order < fieldOrder);
    },
    [fields]
  );

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Toolbox */}
      <Paper
        elevation={0}
        sx={{
          width: 160,
          p: 1.5,
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          overflowY: 'auto',
        }}
      >
        {/* Verification section */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Verification
        </Typography>
        <Box
          draggable={!hasVerifyField}
          onDragStart={e => {
            if (hasVerifyField) return;
            handleDragStart(e, {
              type: 'customer_verify',
              label: 'Verify Customer',
              icon: 'ShieldCheck',
              defaultField: {
                fieldType: 'customer_verify',
                label: 'Verify Customer',
                required: false,
                verificationMethod: 'email',
              },
            });
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 1,
            mb: 1,
            borderRadius: 1,
            border: `1px solid ${hasVerifyField ? theme.palette.action.disabled : theme.palette.warning.main}`,
            backgroundColor: hasVerifyField
              ? theme.palette.action.disabledBackground
              : `${theme.palette.warning.main}10`,
            cursor: hasVerifyField ? 'not-allowed' : 'grab',
            userSelect: 'none',
            opacity: hasVerifyField ? 0.5 : 1,
            '&:hover': hasVerifyField
              ? {}
              : {
                  borderColor: theme.palette.warning.dark,
                  backgroundColor: `${theme.palette.warning.main}20`,
                },
            '&:active': hasVerifyField ? {} : { cursor: 'grabbing' },
          }}
        >
          <GripVertical size={12} color={theme.palette.text.secondary} />
          <ShieldCheck
            size={16}
            color={hasVerifyField ? theme.palette.text.disabled : theme.palette.warning.main}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 500,
              color: hasVerifyField ? theme.palette.text.disabled : theme.palette.warning.dark,
            }}
          >
            Verify Customer
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Drag to add fields
        </Typography>

        {FIELD_TYPES.map(fieldType => (
          <Box
            key={fieldType.type}
            draggable
            onDragStart={e => handleDragStart(e, fieldType)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              p: 1,
              mb: 0.5,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.default,
              cursor: 'grab',
              userSelect: 'none',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: `${theme.palette.primary.main}08`,
              },
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          >
            <GripVertical size={12} color={theme.palette.text.secondary} />
            {getIconComponent(fieldType.icon)}
            <Typography variant="caption">{fieldType.label}</Typography>
          </Box>
        ))}

        <Divider sx={{ my: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          AI
        </Typography>

        <Box
          draggable
          onDragStart={e =>
            handleDragStart(e, {
              type: 'ai',
              label: 'AI Prompt',
              icon: 'Sparkles',
              defaultField: { fieldType: 'ai', label: '', required: false },
            })
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 1,
            mb: 0.5,
            borderRadius: 1,
            border: '1px solid #7C3AED40',
            backgroundColor: '#7C3AED08',
            cursor: 'grab',
            userSelect: 'none',
            '&:hover': {
              borderColor: '#7C3AED',
              backgroundColor: '#7C3AED15',
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <GripVertical size={12} color={theme.palette.text.secondary} />
          <Sparkles size={16} color="#7C3AED" />
          <Typography variant="caption" sx={{ color: '#7C3AED', fontWeight: 500 }}>
            AI Prompt
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Integration
        </Typography>

        <Box
          draggable
          onDragStart={e =>
            handleDragStart(e, {
              type: 'http_request',
              label: 'HTTP Request',
              icon: 'Globe',
              defaultField: { fieldType: 'http_request', label: '', required: false },
            })
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 1,
            mb: 0.5,
            borderRadius: 1,
            border: '1px solid #EA580C40',
            backgroundColor: '#EA580C08',
            cursor: 'grab',
            userSelect: 'none',
            '&:hover': {
              borderColor: '#EA580C',
              backgroundColor: '#EA580C15',
            },
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <GripVertical size={12} color={theme.palette.text.secondary} />
          <Globe size={16} color="#EA580C" />
          <Typography variant="caption" sx={{ color: '#EA580C', fontWeight: 500 }}>
            HTTP Request
          </Typography>
        </Box>
      </Paper>

      {/* ReactFlow Canvas */}
      <Box
        sx={{ flex: 1, backgroundColor: theme.palette.background.default, position: 'relative' }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onConnect={handleConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ style: { strokeWidth: 2 }, focusable: true }}
          fitView={false}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          minZoom={0.5}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={true}
          nodesConnectable={true}
          elementsSelectable={true}
          panOnDrag={true}
          selectionOnDrag={false}
          deleteKeyCode="Backspace"
        >
          <Controls showInteractive={false} />
          <Background
            variant={BackgroundVariant.Dots}
            color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.25)'}
            gap={20}
            size={2}
          />
        </ReactFlow>

        {fields.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Drag fields here to build your form
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Fields will be displayed in order from top to bottom
            </Typography>
          </Box>
        )}
      </Box>

      {/* Insert Field Menu */}
      <Menu
        open={insertMenuAnchor !== null}
        onClose={() => {
          setInsertMenuAnchor(null);
          setInsertContext(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          insertMenuAnchor
            ? { top: insertMenuAnchor.mouseY, left: insertMenuAnchor.mouseX }
            : undefined
        }
      >
        {/* Verify Customer option in insert menu */}
        <MenuItem
          onClick={() =>
            handleInsertFieldType({
              type: 'customer_verify',
              label: 'Verify Customer',
              icon: 'ShieldCheck',
              defaultField: {
                fieldType: 'customer_verify',
                label: 'Verify Customer',
                required: false,
                verificationMethod: 'email',
              },
            })
          }
          disabled={hasVerifyField}
        >
          <ListItemIcon>
            <ShieldCheck size={16} color={theme.palette.warning.main} />
          </ListItemIcon>
          <ListItemText
            sx={{
              '& .MuiTypography-root': {
                color: hasVerifyField ? theme.palette.text.disabled : theme.palette.warning.dark,
                fontWeight: 500,
              },
            }}
          >
            Verify Customer
          </ListItemText>
        </MenuItem>
        <Divider />
        {FIELD_TYPES.map(ft => (
          <MenuItem key={ft.type} onClick={() => handleInsertFieldType(ft)}>
            <ListItemIcon>{getIconComponent(ft.icon)}</ListItemIcon>
            <ListItemText>{ft.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() =>
            handleInsertFieldType({
              type: 'ai',
              label: 'AI Prompt',
              icon: 'Sparkles',
              defaultField: { fieldType: 'ai', label: '', required: false },
            })
          }
        >
          <ListItemIcon>
            <Sparkles size={16} color="#7C3AED" />
          </ListItemIcon>
          <ListItemText sx={{ '& .MuiTypography-root': { color: '#7C3AED', fontWeight: 500 } }}>
            AI Prompt
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() =>
            handleInsertFieldType({
              type: 'http_request',
              label: 'HTTP Request',
              icon: 'Globe',
              defaultField: { fieldType: 'http_request', label: '', required: false },
            })
          }
        >
          <ListItemIcon>
            <Globe size={16} color="#EA580C" />
          </ListItemIcon>
          <ListItemText sx={{ '& .MuiTypography-root': { color: '#EA580C', fontWeight: 500 } }}>
            HTTP Request
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Field Configuration Dialog */}
      <FieldConfigDialog
        open={configDialogOpen}
        onClose={() => {
          setConfigDialogOpen(false);
          setEditingField(null);
          setPendingFieldType(null);
          setInsertPosition(null);
          setInsertContext(null);
        }}
        field={editingField}
        fieldType={pendingFieldType}
        onSave={handleFieldSave}
        existingFieldIds={fields.map(f => f.fieldId)}
        allFields={fields}
      />

      {/* AI Prompt Configuration Dialog */}
      <AIPromptConfigDialog
        open={aiDialogOpen}
        onClose={() => {
          setAiDialogOpen(false);
          setEditingField(null);
          setPendingFieldType(null);
          setInsertPosition(null);
          setInsertContext(null);
        }}
        field={editingField}
        onSave={aiField => {
          handleFieldSave(aiField);
          setAiDialogOpen(false);
        }}
        existingFieldIds={fields.map(f => f.fieldId)}
        allFields={fields}
        hasVerificationBefore={getHasVerificationBefore(editingField?.order ?? fields.length + 1)}
      />

      {/* HTTP Request Configuration Dialog */}
      <HttpRequestConfigDialog
        open={httpDialogOpen}
        onClose={() => {
          setHttpDialogOpen(false);
          setEditingField(null);
          setPendingFieldType(null);
          setInsertPosition(null);
          setInsertContext(null);
        }}
        field={editingField}
        onSave={httpField => {
          handleFieldSave(httpField);
          setHttpDialogOpen(false);
        }}
        existingFieldIds={fields.map(f => f.fieldId)}
        allFields={fields}
        hasVerificationBefore={getHasVerificationBefore(editingField?.order ?? fields.length + 1)}
      />
    </Box>
  );
};

const FormBuilder: React.FC<FormBuilderProps> = props => {
  return (
    <ReactFlowProvider>
      <FormBuilderInner {...props} />
    </ReactFlowProvider>
  );
};

export default FormBuilder;
